/**
 * Knowledge Intent Details Sidebar
 * Lists the queries that belong to one intent. Each query shows its question text, an
 * Auto-send toggle (per-query, via the existing per-unit endpoint), and a collapsible
 * Answer.
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useSetPromotedAutoSend } from '@/hooks/use-promoted-blueprints';
import { usePermissions } from '@/hooks/use-permissions';
import { invalidateQueries, queryClient } from '@/lib/query-client';
import { formatIntentLabel, formatQuery } from '../utils';
import type { KnowledgeBlueprint } from '../types';

interface KnowledgeIntentDetailsSidebarProps {
  intent: string;
  queries: KnowledgeBlueprint[];
  onClose?: () => void;
}

export const KnowledgeIntentDetailsSidebar = ({ intent, queries, onClose }: KnowledgeIntentDetailsSidebarProps) => {
  const setAutoSendMutation = useSetPromotedAutoSend();
  const { can } = usePermissions();
  const canEdit = can('edit', 'promoted-blueprint');
  const [pendingAutoSendId, setPendingAutoSendId] = useState<string | null>(null);

  const queryKey = ['promoted-blueprints', 'knowledge'];

  const handleToggleAutoSend = async (query: KnowledgeBlueprint, autoSend: boolean) => {
    queryClient.setQueryData<KnowledgeBlueprint[]>(queryKey, (prev) =>
      (prev ?? []).map((item) => (item.id === query.id ? { ...item, autoSend } : item))
    );
    setPendingAutoSendId(query.id);
    try {
      await setAutoSendMutation.mutateAsync({ id: query.id, autoSend });
      toast.success(autoSend ? 'Auto-send enabled' : 'Auto-send disabled');
    } catch (error) {
      queryClient.setQueryData<KnowledgeBlueprint[]>(queryKey, (prev) =>
        (prev ?? []).map((item) => (item.id === query.id ? { ...item, autoSend: !autoSend } : item))
      );
      toast.error('Failed to update query');
      console.error('Toggle query auto-send error:', error);
    } finally {
      setPendingAutoSendId(null);
      invalidateQueries(queryKey);
    }
  };

  return (
    <aside className='flex h-full w-[420px] shrink-0 flex-col border-l bg-background'>
      <div className='flex items-start justify-between gap-2 border-b px-5 py-4'>
        <div className='min-w-0'>
          <p className='text-[11px] font-medium uppercase tracking-wide text-muted-foreground'>Knowledge Intent</p>
          <h2 className='truncate text-sm font-semibold text-foreground'>{formatIntentLabel(intent)}</h2>
          <p className='truncate font-mono text-[11px] text-muted-foreground'>{intent}</p>
        </div>
        {onClose && (
          <Button variant='ghost' size='icon' className='size-7 shrink-0' onClick={onClose}>
            <X className='size-4' />
          </Button>
        )}
      </div>

      <div className='flex-1 overflow-y-auto px-5 py-4'>
        <div className='mb-1 flex items-center justify-between'>
          <h3 className='text-xs font-semibold text-foreground'>Queries</h3>
          <span className='text-[11px] text-muted-foreground'>
            {queries.length} {queries.length === 1 ? 'query' : 'queries'}
          </span>
        </div>
        <p className='mb-3 text-[11px] leading-relaxed text-muted-foreground'>
          <span className='font-medium text-foreground'>Auto-send on</span> = a matching question can auto-send.{' '}
          <span className='font-medium text-foreground'>Off</span> = answered as a draft for human review.
        </p>

        <div className='space-y-3'>
          {queries.length === 0 && <p className='text-xs text-muted-foreground'>No queries for this intent.</p>}
          {queries.map((query) => {
            const question = formatQuery(query.question) || formatIntentLabel(query.intent);
            return (
              <div key={query.id} className='rounded-lg border'>
                <div className='flex items-start justify-between gap-3 border-b bg-muted/40 px-3 py-2'>
                  <p className='min-w-0 flex-1 text-xs font-medium text-foreground' title={question}>
                    {question}
                  </p>
                  <div className='flex shrink-0 items-center gap-2'>
                    <span className='text-[11px] text-muted-foreground'>Auto-send</span>
                    <Switch
                      className='cursor-pointer'
                      checked={query.autoSend}
                      onCheckedChange={(checked) => handleToggleAutoSend(query, checked)}
                      disabled={!canEdit || pendingAutoSendId === query.id}
                    />
                    <span className='w-6 text-[11px] font-medium text-foreground'>{query.autoSend ? 'Yes' : 'No'}</span>
                  </div>
                </div>
                <Accordion type='single' collapsible className='px-3'>
                  <AccordionItem value='answer' className='border-b-0'>
                    <AccordionTrigger className='py-2 text-xs font-semibold'>Answer</AccordionTrigger>
                    <AccordionContent>
                      <div className='max-h-64 overflow-y-auto whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-[13px] leading-relaxed text-foreground/90'>
                        {query.template || <span className='italic text-muted-foreground'>No answer content.</span>}
                      </div>
                      {query.responsePolicy !== undefined && query.responsePolicy !== null && (
                        <pre className='mt-2 whitespace-pre-wrap rounded-md bg-muted p-3 text-[12px] leading-relaxed text-muted-foreground'>
                          {typeof query.responsePolicy === 'string'
                            ? query.responsePolicy
                            : JSON.stringify(query.responsePolicy, null, 2)}
                        </pre>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
};
